import {
  createScheduleBlock,
  createTask,
  getSchedule,
  type CommitmentLevel,
  type ContentQueueItem,
} from "@/lib/api";
import type { ReviewItem } from "@/lib/api/projection-types";
import {
  buildContentScheduleTitle,
  buildContentTaskDescription,
  buildContentTaskTitle,
  findNextOpenScheduleSlot,
} from "@/components/review/reviewActionHelpers";
import { formatLocalDateInput } from "@/lib/utils";

export type PlacementChoice = "now" | "planned" | "later";

export interface PlaceCoreResult {
  taskId: string;
  scheduleBlockId: string | null;
  scheduledStart: Date | null;
  scheduledEnd: Date | null;
  plannedDate: string;
}

export interface PlaceCoreContext {
  resourceDetails: Record<string, ContentQueueItem>;
  notifyTaskChange: () => void;
  notifyScheduleChange: () => void;
}

export function fallbackResourceDetail(item: ReviewItem): ContentQueueItem {
  return {
    id: item.id,
    title: item.title,
    url: null,
    content_type: "article",
    duration_min: 25,
    goal_id: item.goal_id ?? null,
    goal_title: item.goal_title ?? null,
    goal_category: null,
    goal_match_pct: null,
    priority_score: 0,
    status: "queued",
    postpone_count: 0,
    priority_reason: null,
    saved_at: null,
  };
}

export function choiceToCommitment(choice: PlacementChoice): CommitmentLevel {
  if (choice === "later") return "flexible";
  return "planned";
}

function isAfterPlanningWindow(targetDate: string) {
  const now = new Date();
  if (formatLocalDateInput(now) !== targetDate) return false;
  return now >= new Date(`${targetDate}T20:00:00`);
}

export async function placeCore(
  item: ReviewItem,
  choice: PlacementChoice,
  ctx: PlaceCoreContext,
): Promise<PlaceCoreResult> {
  const detail = ctx.resourceDetails[item.id] ?? fallbackResourceDetail(item);
  const taskTitle = buildContentTaskTitle(detail);
  const taskDescription = buildContentTaskDescription(detail);
  const estimatedMinutes = detail.duration_min || 25;
  const commitmentLevel = choiceToCommitment(choice);

  const task = await createTask({
    title: taskTitle,
    description: taskDescription,
    goal_id: detail.goal_id || undefined,
    commitment_level: commitmentLevel,
    estimated_minutes: estimatedMinutes,
    tags: [detail.content_type || "resource", "review-backlog"],
  });
  ctx.notifyTaskChange();

  if (choice !== "planned") {
    return {
      taskId: task.id,
      scheduleBlockId: null,
      scheduledStart: null,
      scheduledEnd: null,
      plannedDate: formatLocalDateInput(new Date()),
    };
  }

  const targetDate = formatLocalDateInput(new Date());
  const currentSchedule = await getSchedule(targetDate).catch(() => null);
  let plannedDate = targetDate;
  let slot = currentSchedule
    ? findNextOpenScheduleSlot(currentSchedule.blocks, estimatedMinutes, targetDate)
    : null;

  if (!slot && isAfterPlanningWindow(targetDate)) {
    const nextDateValue = new Date(`${targetDate}T12:00:00`);
    nextDateValue.setDate(nextDateValue.getDate() + 1);
    const nextDate = formatLocalDateInput(nextDateValue);
    const nextSchedule = await getSchedule(nextDate).catch(() => null);
    const nextSlot = nextSchedule
      ? findNextOpenScheduleSlot(nextSchedule.blocks, estimatedMinutes, nextDate)
      : null;
    if (nextSlot) {
      plannedDate = nextDate;
      slot = nextSlot;
    }
  }

  if (!slot) {
    return {
      taskId: task.id,
      scheduleBlockId: null,
      scheduledStart: null,
      scheduledEnd: null,
      plannedDate,
    };
  }

  const scheduleBlock = await createScheduleBlock({
    kind: "custom",
    title: buildContentScheduleTitle(detail),
    description: taskDescription,
    commitment_level: commitmentLevel,
    task_id: task.id,
    goal_id: detail.goal_id || undefined,
    goal_title: detail.goal_title || undefined,
    color: "#D39A55",
    start_time: slot.start.toISOString(),
    end_time: slot.end.toISOString(),
  });
  ctx.notifyScheduleChange();

  return {
    taskId: task.id,
    scheduleBlockId: scheduleBlock.id,
    scheduledStart: slot.start,
    scheduledEnd: slot.end,
    plannedDate,
  };
}

export function buildPlannedDateLabel(targetDate: string) {
  const today = formatLocalDateInput(new Date());
  if (targetDate === today) return "today";
  return new Date(`${targetDate}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
